/**
 * URLs for the league image CDN — player photos and team crests.
 *
 * The CDN moved from report.dpl.gg to img.dust2.org in September 2026. The paths are unchanged
 * (still named .php, no longer PHP), and the old host still answers, but only the new one honours
 * the newer query params — so everything we build points at the new host.
 *
 * Team crests are normalised server-side (Models/Team.TeamLogo): the API already hands us the
 * current host and the `letter=` fallback. Only URLs saved in our own data before the move (tier
 * lists keep their cards' logos) need rehosting here, via `rehostImage`.
 */

export const CDN_HOST = "https://img.dust2.org";
const LEGACY_HOST = "https://report.dpl.gg";

const PLAYER_PATH = "/powerfiles/ligaen/getPlayerPicture.php";

/**
 * The date part of a match_time (`"2025-03-12T20:00:00"` — UTC wall-clock digits with no
 * timezone suffix; see utils/matchTime.ts), or undefined if it is not one.
 *
 * Deliberately string surgery rather than `new Date(...)`: parsing it as UTC and reformatting
 * with `.toLocaleDateString()`/`.toISOString()` could shift it a day for late-evening matches.
 * The CDN answers HTTP 400 to a malformed `at`, so anything unrecognised has to drop the param,
 * never pass it through.
 */
export const photoDate = (matchTime?: string | null): string | undefined => {
	const day = matchTime?.split("T")[0];
	return day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : undefined;
};

/**
 * A player's photo from the league CDN.
 *
 * `at` asks for the photo as it was on that date instead of today's — so an old match shows the
 * player as they looked then. A date in the future simply returns the current photo.
 */
export const playerPhotoUrl = (id: string, width: number, at?: string | null): string => {
	const day = photoDate(at);
	return `${CDN_HOST}${PLAYER_PATH}?id=${id}&width=${width}${day ? `&at=${day}` : ""}`;
};

/** Moves a CDN URL saved before the move onto the current host; report.dpl.gg no longer answers. */
export const rehostImage = (url?: string): string | undefined =>
	url && url.startsWith(LEGACY_HOST) ? CDN_HOST + url.slice(LEGACY_HOST.length) : url;
